import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/app/Brand";
import HeroBackdrop from "@/components/app/HeroBackdrop";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0D1117]">
      <div className="hidden lg:block relative border-r border-[#1F2937] bg-[#060D1A] text-white overflow-hidden">
        <HeroBackdrop />
      </div>
      <div className="flex flex-col p-6 sm:p-10">
        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-gray-500 flex items-center gap-1 hover:text-white w-fit" data-testid="back-home-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </Link>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <LogoMark />
            </div>

          {sent ? (
            <div data-testid="forgot-sent">
              <div className="flex items-center gap-2 text-emerald-700 mb-3">
                <CheckCircle2 className="w-5 h-5" />
                <h1 className="font-display font-black text-2xl text-white tracking-tighter">Check your inbox</h1>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                If an account exists for <span className="font-semibold text-white">{email}</span>, we&apos;ve just sent a password-reset link. It expires in 30 minutes.
              </p>
              <p className="text-xs text-gray-500 mt-4">
                Didn&apos;t receive anything? Check your spam folder or{" "}
                <button onClick={() => setSent(false)} className="text-[#002FA7] font-semibold" data-testid="forgot-try-again">try again</button>.
              </p>
              <div className="mt-8">
                <Link to="/auth" className="text-sm text-[#002FA7] font-semibold" data-testid="back-to-signin">← Back to sign in</Link>
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] mb-2">Account recovery</div>
              <h1 className="font-display font-black text-3xl text-white tracking-tighter mb-2">Reset your password.</h1>
              <p className="text-sm text-slate-400 mb-8">
                Enter your account email — we&apos;ll send you a link to choose a new password.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
                <div>
                  <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded-none mt-1 h-11"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" disabled={busy} className="rounded-none w-full h-11 bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="forgot-submit-btn">
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
              </form>
              <div className="mt-6 text-sm text-center text-slate-400">
                Remembered it?{" "}
                <Link to="/auth" className="text-[#002FA7] font-semibold" data-testid="back-to-signin-inline">Sign in</Link>
              </div>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
